import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import AppContext from "../../AppContext";
import PanoramaInfo from "./PanoramaInfo";
import WorkExchangeInfo from "./WorkExchangeInfo";
import Stepper from "react-stepper-horizontal";
import NewProjectContext from "../../contexts/NewProjectContext";
import UploadPanoramas from "./UploadPanoramas";
import UploadImages from "./UploadImages";
import SetupHotspot from "./SetupHotspot/index.jsx";
import UploadWorks from "./UploadFiles";
import SelectCover from "./SelectCover";
import Summary from "./Summary";
import Description from "./WorkExchange/Description.jsx";
import ProjectFile from "./WorkExchange/ProjectFile.jsx";
import ProjectCover from "./WorkExchange/ProjectCover.jsx";
import WorkExchangeSummary from "./WorkExchange/Summary.jsx";

import "../../styles/newProject.scss";
import SelectType from "./SelectType";
import { langComponent } from "../../constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import _ from "lodash";
import useWorks from "../../hooks/useWorks.js";
import { uuidv4 } from "../../helpers/common.helper.js";

const assetsURI = import.meta.env.ASSETS_URI;

const STEPS = {
  panorama: {
    TYPE: 0,
    PANORAMA_INFO: 1,
    HOTSPOT: 2,
    COVER: 3,
    FINISH: 4,
  },
  other: {
    TYPE: 0,
    PANORAMA_INFO: 1,
    UPLOAD: 2,
    COVER: 3,
    FINISH: 4,
  },
  workExchange: {
    TYPE: 0,
    DESCRIPTION: 1,
    WORK_EXCHANGE_FINISH: 2,
  },
  tendering: {
    TYPE: 0,
    TENDERING_INFO: 1,
    TENDERING_GALLERY: 2,
    TENDERING_COVER: 3,
    TENDERING_FINISH: 4,
  },
};

const PROGRAMS = {
  1: "sketchUp",
  2: "3dsMax",
  3: "revit",
  4: "cinema4D",
  5: "unreal",
  6: "rhino",
  7: "autocad",
  9: "archicad",
};

const RENDERERS = {
  1: "vray",
  2: "corona",
  3: "lumion",
  4: "enscape",
  5: "d5render",
};

const NewProject = ({ onCancel, onSuccess }) => {
  const {
    isAuth,
    getLang,
    designStyles: _designStyles,
  } = useContext(AppContext);
  const Translation = getLang(langComponent.project);
  const [details, setDetails] = useState({});
  const [stepsToCompare, setStepsToCompare] = useState(undefined);
  const [stepperSize, setStepperSize] = useState("l");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getWork } = useWorks();
  const [edit] = useState(searchParams.get("edit"));
  const [step, setStep] = useState(edit ? undefined : 0);

  const stepperSteps = {
    panorama: [
      { title: Translation("Information") },
      { title: Translation("Setup Hotspots") },
      { title: Translation("Select cover") },
      { title: Translation("Finish") },
    ],
    other: [
      { title: Translation("Information") },
      { title: Translation("Upload") },
      { title: Translation("Select cover") },
      { title: Translation("Finish") },
    ],
    workExchange: [
      // { title: Translation("Information") },
      { title: Translation("Information") },
      // { title: Translation("Payment Option") },
      // { title: Translation("Project cover") },
      { title: Translation("Finish") },
    ],
    tendering: [
      { title: Translation("Information") },
      { title: Translation("Image gallery") },
      { title: Translation("Project cover") },
      { title: Translation("Finish") },
    ],
  };

  useEffect(() => {
    // * Navigate back to home if not auth
    if (!isAuth) {
      navigate("/");
      return;
    }

    if (!_designStyles) {
      return;
    }

    init();

    return () => {
      setDetails({});
    };
  }, [_designStyles]);

  const contentRef = useCallback((node) => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver((entries) => {
        const _width = entries[0].contentRect.width;

        if (_width > 600) {
          setStepperSize("l");
          return;
        }

        if (_width > 400) {
          setStepperSize("m");
          return;
        }

        // if (_width > 400) {
        //   setStepperSize("m");
        //   return;
        // }
      });
      resizeObserver.observe(node);
    }
  }, []);

  useEffect(() => {
    if (!details.type) {
      return;
    }

    if (details.type === "panorama") {
      setStepsToCompare(STEPS.panorama);
      return;
    }

    if (
      ["3D Models", "Materials and lighting", "Drawing and block CAD"].includes(
        details.type
      )
    ) {
      setStepsToCompare(STEPS.workExchange);
      return;
    }

    if (details.type === "tendering") {
      setStepsToCompare(STEPS.tendering);
      return;
    }

    setStepsToCompare(STEPS.other);
    return () => {};
  }, [details]);

  const init = async () => {
    // * Check if is edit
    if (!edit) return;

    // * Get work data and transform to detail state
    const {
      _id,
      name,
      desc,
      program,
      renderer,
      designType,
      type,
      size,
      price,
      discount,
      levels,
      location,
      panorama,
      floorPlans,
      music,
      images,
      workFileTypes,
      banner,
      files,
      keywords,
    } = await getWork(edit);

    let _type = undefined;

    // * Determine work type
    if (type === 1) _type = "panorama";
    if (type === 3) _type = "images";
    if (type === 2)
      _type = _designStyles.workExchanges.find(
        (we) => we.value === designType.workExchange
      ).name;

    // * Panorama images and hotspots
    const _panoramaImages = [];
    const _panoramaCovers = [];
    const _scenes = [];

    if (type === 1) {
      for (let i = 0; i < panorama.images.length; i++) {
        const { _id, image, hotspots, name } = panorama.images[i];

        const response = await fetch(`${assetsURI}/${image}`, {
          cache: "no-cache",
        });
        const blob = await response.blob();
        const file = new File([blob], _id, {
          type: blob.type,
        });

        _panoramaImages.push(file);

        const _scene = {
          name: name === "null" ? "" : name,
          imageIndex: i,
          imageName: _id,
          hotspots: [],
        };

        for (const hotspot of hotspots) {
          const {
            _id,
            type,
            tooltip,
            pitch,
            yaw,
            icon,
            panorama: _panoramaId,
          } = hotspot;

          const _hotspot = {
            id: uuidv4(),
            type,
            title: tooltip.title,
            desc: tooltip.desc,
            pitch,
            yaw,
            icon: icon.internal,
            customIcon: null,
            sceneIndex: panorama.images.findIndex((_) => _._id === _panoramaId),
            sceneName: _panoramaId,
          };

          _scene.hotspots.push(_hotspot);
        }

        _scenes.push(_scene);
      }
    }

    // * Map Images
    let cover = undefined;
    const _images = [];

    if (type === 3) {
      for (let i = 0; i < images.length; i++) {
        const { _id, path } = images[i];

        const response = await fetch(`${assetsURI}/${path}`, {
          cache: "no-cache",
        });
        const blob = await response.blob();
        const file = new File([blob], _id, {
          type: blob.type,
        });

        _images.push(file);
      }
    }

    if (type === 2) {
      for (let i = 0; i < images.length; i++) {
        const { _id: imageId, path } = images[i];

        const response = await fetch(`${assetsURI}/${path}`, {
          cache: "no-cache",
        });

        const blob = await response.blob();
        const file = new File([blob], imageId, {
          type: blob.type,
        });

        _images.push({ _id: imageId, path: file, type: "file" });
      }
    }

    // * Work files
    const _files = [];

    // const _workFiles = await getFileWork(edit);

    // for (const file of _workFiles) {
    //   const res = await fetch(file.link, {
    //     cache: "no-cache",
    //   });
    //   const blob = await res.blob();

    //   _files.push(
    //     new File([blob], file.name, {
    //       type: blob.type,
    //     })
    //   );
    // }

    // * Map floorplans
    const _floorPlans = [];
    const _floorPlanNames = [];

    for (const fp of floorPlans) {
      const { _id, file, mimeType, name } = fp;

      const imageBlob = await (
        await fetch(`${assetsURI}/${file}`, {
          cache: "no-cache",
        })
      ).blob();
      _floorPlans.push(new File([imageBlob], name, { type: mimeType }));
      _floorPlanNames.push({
        fileName: name,
        name: name,
      });
    }

    setDetails({
      type: _type,
      info: {
        name,
        desc,
        area: size,
        price: price || "",
        program: PROGRAMS[program],
        renderer: RENDERERS[renderer],
        work: designType.work,
        space: designType.space,
        style: designType.style,
        location,
      },
      panoramaImages: _panoramaImages,
      scenes: _scenes,
      floorPlans: _floorPlans,
      floorPlanNames: _floorPlanNames,
      music,
      images: _images,
      workExchange: {
        info: {
          name,
          desc,
          program: PROGRAMS[program],
          renderer: RENDERERS[renderer],
          fileTypes: workFileTypes,
          type: designType.workExchange,
          category: designType.workExchangeCategory,
          subCategory: designType.workExchangeSubCategory,
          materials: designType.workExchangeMaterials ?? [],
          keywords,
          price,
          discount,
        },
        cover: {
          dataUrl: `${assetsURI}/${banner}`,
        },
        images: _images,
        files: _files,
      },
      workType: designType.workExchange,
    });

    setStep(1);
  };

  const getRender = () => {
    const compareType = {
      panorama: STEPS.panorama,
      tendering: STEPS.tendering,
      "3D Models": STEPS.workExchange,
      "Materials and lighting": STEPS.workExchange,
      "Drawing and block CAD": STEPS.workExchange,
      images: STEPS.other,
    };

    const stepsToCompare = compareType[details?.type ?? "panorama"];

    if (step === stepsToCompare.TYPE) {
      return <SelectType />;
    }

    if (step === stepsToCompare.PANORAMA_INFO) {
      return <PanoramaInfo />;
    }

    if (step === stepsToCompare.UPLOAD) {
      return <UploadImages />;
    }

    if (step === stepsToCompare.PANORAMA) {
      return <UploadPanoramas />;
    }

    if (step === stepsToCompare.HOTSPOT) {
      return <SetupHotspot />;
    }

    if (step === stepsToCompare.WORK) {
      return <UploadWorks />;
    }

    if (step === stepsToCompare.COVER) {
      return <SelectCover />;
    }

    if (step === stepsToCompare.FINISH) {
      return <Summary />;
    }

    // * Work Exchange Specific
    if (step === stepsToCompare.WORK_EXCHANGE_INFO) {
      return <WorkExchangeInfo />;
    }
    if (step === stepsToCompare.DESCRIPTION) {
      return <Description />;
    }
    if (step === stepsToCompare.FILES) {
      return <ProjectFile />;
    }
    // if (step === stepsToCompare.PRICE) {
    //   return <Price />;
    // }
    if (step === stepsToCompare.WORK_EXCHANGE_COVER) {
      return <ProjectCover />;
    }
    if (step === stepsToCompare.WORK_EXCHANGE_FINISH) {
      return <WorkExchangeSummary />;
    }
  };

  const handleSuccess = (workId) => {
    onSuccess(workId);
  };

  const handleGoForward = () => {
    window.scrollTo(0, 0);
    setStep((curr) => curr + 1);
  };

  const handleGoBackward = () => {
    if (step === 0) {
      // * Navigate back to profile
      onCancel();
      return;
    }

    if (step === 1 && (edit || details._id)) {
      // * Navigate back to profile
      onCancel();
      return;
    }

    setStep((curr) => curr - 1);
    window.scrollTo(0, 0);
  };

  const contextValues = useMemo(
    () => ({
      step,
      goForward: handleGoForward,
      goBackward: handleGoBackward,
      details,
      setDetails,
      onSuccess: handleSuccess,
      edit,
    }),
    [step, details, edit]
  );

  const stepperType = {
    panorama: stepperSteps.panorama,
    tendering: stepperSteps.tendering,
    "3D Models": stepperSteps.workExchange,
    "Materials and lighting": stepperSteps.workExchange,
    "Drawing and block CAD": stepperSteps.workExchange,
    images: stepperSteps.other,
  };

  const stepCondition = () => {
    if (details?.type) {
      return details?.type;
    }

    return "panorama";
  };

  const _steps = stepperType[stepCondition()];

  if (step === undefined) return null;

  return (
    <NewProjectContext.Provider value={contextValues}>
      <div className="new-project" ref={contentRef}>
        {stepsToCompare && step !== stepsToCompare.TYPE && (
          <div className="new-project-steps">
            {stepperSize === "l" ? (
              <Stepper
                steps={_steps}
                activeStep={step - 1}
                activeColor="#013565"
                completeColor="#013565"
                activeTitleColor="#013565"
                completeBarColor="#013565"
                completeTitleColor="#013565"
                defaultColor="#BEBEBE"
                defaultBarColor="#BEBEBE"
                defaultTitleColor="#9D9D9D"
                circleFontSize={16}
                titleFontSize={12}
                titleTop={8}
                lineMarginOffset={0}
              />
            ) : (
              <div className="new-project-steps-compact">
                <span className="new-project-steps-compact__step">{`Step ${step}/${_steps.length}`}</span>
                <span className="new-project-steps-compact__title">
                  {_steps[step - 1].title}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="new-project-content">{getRender()}</div>
      </div>
    </NewProjectContext.Provider>
  );
};

NewProject.propTypes = {
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
};

NewProject.defaultProps = {
  onCancel: () => {},
  onSuccess: () => {},
};

export default NewProject;
